import { ReportType } from '../types/model/common';
import {
  ReportPageEstimateInput,
} from './buildReportPageEstimateInput';
import { ReportIssueEstimateRow } from './reportIssueEstimateRow';
import {
  approvedByNode,
  buildIssueBodyNodes,
  buildSingleIssueNodes,
} from './layout/buildIssueLayoutNodes';
import {
  column,
  countLayoutPages,
  LayoutNode,
  leaf,
  nextPage,
} from './layout/pdfLayoutEngine';
import {
  CONTENT_WIDTH,
  FONT_SIZE,
  USABLE_HEIGHT,
} from './layout/pdfLayoutConstants';
import {
  estimatePlainTextHeight,
  singleLineHeight,
} from './layout/measurePdfText';

export type ReportPageEstimateResult = {
  pages: number;
  progressPercent: number;
};

/** Reference scale for the progress bar only — not a display cap. */
export const REPORT_PAGE_ESTIMATE_PROGRESS_REFERENCE_PAGES = 200;

const COVER_TITLE_HEIGHT = 40;
const COVER_STATS_HEIGHT = 220;
const ISSUE_LIST_TITLE_ROW_HEIGHT = 26;

function reportMessageNode(input: ReportPageEstimateInput): LayoutNode | undefined {
  if (!input.reportMessageInPdf || input.reportMessageChars <= 0) {
    return undefined;
  }
  const height =
    singleLineHeight(FONT_SIZE.label) +
    estimatePlainTextHeight(
      'x'.repeat(input.reportMessageChars),
      FONT_SIZE.content,
      CONTENT_WIDTH
    );
  return leaf(height);
}

/**
 * Approximates the backend `mainGroupPage` — one map/cover page per distinct site+level
 * group that pins issues to a plan. The estimate input has no plan-presence data, so we
 * key on issues that carry a map position. Conservative; documented known approximation.
 */
function groupMapPageCount(issueRows: ReportIssueEstimateRow[]): number {
  const groups = new Set<string>();
  for (const { issue } of issueRows) {
    if (!issue.primaryData.location) {
      continue;
    }
    const site = issue.primaryData.site ?? '';
    const level = issue.primaryData.level ?? '';
    groups.add(`${site}::${level}`);
  }
  return groups.size;
}

function buildIssueListTree(input: ReportPageEstimateInput): LayoutNode {
  const children: LayoutNode[] = [leaf(COVER_TITLE_HEIGHT)];

  const messageNode = reportMessageNode(input);
  if (messageNode) {
    children.push(messageNode);
  }
  children.push(leaf(COVER_STATS_HEIGHT));

  const mapPages = groupMapPageCount(input.issueRows);
  for (let i = 0; i < mapPages; i += 1) {
    children.push(nextPage(), leaf(USABLE_HEIGHT));
  }

  for (const row of input.issueRows) {
    children.push(nextPage());
    children.push(leaf(ISSUE_LIST_TITLE_ROW_HEIGHT));
    children.push(...buildIssueBodyNodes(row, input));
  }

  children.push(approvedByNode());

  return column(children);
}

function buildSingleIssueTree(input: ReportPageEstimateInput): LayoutNode {
  const row = input.issueRows[0];
  if (!row) {
    return column([leaf(COVER_STATS_HEIGHT)]);
  }
  return column(buildSingleIssueNodes(row, input));
}

function progressReferencePages(input: ReportPageEstimateInput): number {
  if (input.reportKind === ReportType.singleIssue) {
    return REPORT_PAGE_ESTIMATE_PROGRESS_REFERENCE_PAGES;
  }
  return Math.max(
    REPORT_PAGE_ESTIMATE_PROGRESS_REFERENCE_PAGES,
    input.issueRows.length * 3
  );
}

export function estimateReportPages(
  input: ReportPageEstimateInput
): ReportPageEstimateResult {
  const tree =
    input.reportKind === ReportType.issueList
      ? buildIssueListTree(input)
      : buildSingleIssueTree(input);

  const pages = Math.max(1, countLayoutPages(tree));
  const progressReference = progressReferencePages(input);

  return {
    pages,
    progressPercent: Math.min(
      100,
      Math.round((pages / progressReference) * 100)
    ),
  };
}

export function formatEstimatedPageRange(pages: number): string {
  const low = Math.max(1, Math.round(pages * 0.9));
  const high = Math.max(low, Math.round(pages * 1.1));
  if (low === high) {
    return String(low);
  }
  return `${low}–${high}`;
}
